import type { CreditReason, Customer, CustomerSummary, Invoice, InvoiceCreate, InvoiceEdit, OrderDayPreview, PriceSheetRow } from '@morales/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ASSIST_STATE, type AssistState } from '../components/ClaudeButton';
import { api, ApiError, newIdempotencyKey } from '../api';
import { useAuth } from '../auth';
import { filterOptions, Sheet, type PickOption, type SheetColumn } from '../components/Sheet';
import { InvoiceMakingBand } from '../components/QuickBooksBand';
import { ErrorBox } from '../components/ui';
import { addDays, formatDate, formatMoney, formatQty, parseTyped, roundCents, todayIso } from '../format';
import { useUnsaved } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

/** One line of an invoice being typed: the product, how many, and a rate only when someone typed one. */
export interface InvLine {
  key: string;
  itemId: string | null;
  qty: number | null;
  /** typed by a person; null = the customer's price from the engine */
  rate: number | null;
  /** the item's pack unit, kept when the product is picked; LB, EA or BN when sold out of the box (decision 166) */
  unit?: string;
  /** editing a saved invoice (decision 170): the line it is, while its product and unit stay; and its name as printed */
  lineId?: string;
  desc?: string;
  /** the quantity the saved line already took out of stock: on hand counts it as still there for this line */
  saved?: number;
  /** the order's line it is (the order day, decision 175), while its product and unit stay */
  orderLineNo?: number;
  /** the unit and quantity before the last unit switch: switching back with no quantity typed meanwhile restores them
   *  exactly (CS → lb → CS never drifts by a rounding) */
  switchedFrom?: { unit: string; qty: number | null };
  /** the vendors short of it for the load (decision 176); the person types the new number */
  vendorShorts?: OrderDayPreview['lines'][number]['vendorShorts'];
  /** a credit line (a rate below zero, JL's way, decision 188): why it is given */
  creditReason?: CreditReason;
}
/** A rate below zero is a credit (decision 188): no case off the shelf, not picked, sent apart from the lines. */
export const isCredit = (l: InvLine): boolean => l.rate !== null && l.rate < 0;
export const CREDIT_REASONS: CreditReason[] = ['quality', 'short', 'price', 'returned', 'goodwill', 'other'];
/** another product or unit on a saved line is a new line: the old one comes off when saved */
const fresh = ({ lineId: _line, desc: _desc, saved: _saved, orderLineNo: _no, vendorShorts: _short, creditReason: _why, ...l }: InvLine): InvLine => l;

let seq = 0;
export const blankLine = (): InvLine => ({ key: `l${(seq += 1)}`, itemId: null, qty: null, rate: null });
const parseNum = (s: string): number | null => parseTyped(s, { negative: true });
const round2 = roundCents;
/** up to the cent, as the server holds a typed price (0.8125 a pound needs 0.82) */
const ceilCents = (x: number): number => Math.ceil(x * 100 - 1e-9) / 100;
const isBlank = (l: InvLine) => l.itemId === null && l.qty === null;

/** Keep the typed lines in order and exactly one blank line at the end, like QuickBooks. */
export function withTrailingBlank(lines: InvLine[]): InvLine[] {
  const typed = lines.filter((l) => !isBlank(l));
  return [...typed, lines.length && isBlank(lines[lines.length - 1]!) ? lines[lines.length - 1]! : blankLine()];
}

const CHANNELS = ['phone', 'sms', 'whatsapp', 'walkin'] as const;
type Channel = (typeof CHANNELS)[number];
type After = 'new' | 'print' | 'close';
/** "Oct 1": the month and the day, enough beside a price */
const shortDate = (iso: string, intlLocale: string) => new Intl.DateTimeFormat(intlLocale, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${iso}T12:00:00Z`));

/** One invoice on the screen: what was typed, and where its save stands. */
export interface Card {
  key: string;
  customerId: string | null;
  invoiceDate: string;
  channel: Channel;
  notes: string;
  lines: InvLine[];
  approveFloor: boolean;
  overrideHold: boolean;
  /** the key of the last try, kept while the same body is retried (a lost answer never makes two invoices) */
  attempt: { body: string; key: string } | null;
  saving: boolean;
  error: unknown;
  done: Invoice | null;
  /** a saved invoice being changed (decision 170): which one, and the total it was opened on */
  edit?: { id: string; invoiceNo: number; invoiceDate: string; total: number; updatedAt: string | undefined; fromQuickBooks?: boolean };
  /** the number typed on a saved invoice (decision 202): null while the box is empty */
  invoiceNo?: number | null;
  /** the invoice has credits for shorts: the person says the cases taken off now are other cases */
  confirmCredits?: boolean;
  /** an order already taken (the order day, decision 175): it is the one invoiced, with these lines */
  orderId?: string;
  orderNo?: number;
  /** the order as the order day saw it; picked or further along, it is invoiced as it is */
  orderVersion?: string;
  orderLocked?: boolean;
}
/** The order day hands its orders to this screen under this key (decision 175). */
export const ORDER_DAY_STATE = 'orderDayInvoices';
/** held: standing customers not drafted (on hold or over their credit limit), named so nobody is missed */
type OrderDayState = { orders: OrderDayPreview[]; back: string; held?: string[] };
let cardSeq = 0;
export const newCard = (customerId: string | null = null): Card => ({
  key: `c${(cardSeq += 1)}`, customerId, invoiceDate: todayIso(), channel: 'phone', notes: '', lines: [blankLine()],
  approveFloor: false, overrideHold: false, attempt: null, saving: false, error: null, done: null,
});
/** A saved invoice opened to change it: its lines at their prices, typed as they are (decision 170). */
export const editCard = (inv: Invoice): Card => ({
  ...newCard(inv.customerId),
  invoiceDate: inv.invoiceDate,
  notes: inv.notes ?? '',
  // a line with no product (a fee) and one shorted to nothing are not on this screen and stay on the invoice as they are
  // a credit line (decision 188) comes back as its rate below zero with its reason; it holds no stock
  lines: withTrailingBlank(
    inv.lines
      .filter((l) => l.itemId !== null && Number(l.qty) > 0 && (Number(l.unitPrice) >= 0 || !!l.creditReason))
      .map((l) => ({ ...blankLine(), itemId: l.itemId, qty: Number(l.qty), rate: Number(l.unitPrice), unit: l.unit ?? 'CS', lineId: l.id, desc: l.description, ...(l.creditReason ? { creditReason: l.creditReason } : { saved: Number(l.qty) }) })),
  ),
  edit: { id: inv.id, invoiceNo: inv.invoiceNo, invoiceDate: inv.invoiceDate, total: inv.total, updatedAt: inv.updatedAt, fromQuickBooks: inv.fromQuickBooks === true },
  invoiceNo: inv.invoiceNo,
});
const filledOf = (c: Card) => c.lines.filter((l) => l.itemId !== null);
/** the lines that bill goods, and the credit lines (decision 188), as they are sent */
const goodsOf = (c: Card) => filledOf(c).filter((l) => !isCredit(l));
const creditsOf = (c: Card) => filledOf(c).filter(isCredit);
const qtyMissingOf = (c: Card) => filledOf(c).filter((l) => l.qty === null || l.qty <= 0);
const reasonMissingOf = (c: Card) => creditsOf(c).filter((l) => !l.creditReason);
const readyOf = (c: Card) => !c.done && !!c.customerId && goodsOf(c).length > 0 && qtyMissingOf(c).length === 0 && reasonMissingOf(c).length === 0;
const creditBody = (l: InvLine) => ({ itemId: l.itemId!, qty: l.qty!, unit: (l.unit ?? 'CS') as InvoiceCreate['lines'][number]['unit'], unitPrice: -l.rate!, reason: l.creditReason! });

/** What a card sends: only typed rates travel; the rest the server prices. */
export function bodyOf(c: Card, owner: boolean): InvoiceCreate {
  return {
    customerId: c.customerId!,
    invoiceDate: c.invoiceDate,
    channel: c.channel,
    ...(c.notes.trim() ? { notes: c.notes.trim() } : {}),
    lines: goodsOf(c).map((l) => ({ itemId: l.itemId!, qty: l.qty!, unit: (l.unit ?? 'CS') as InvoiceCreate['lines'][number]['unit'], ...(l.rate !== null ? { unitPrice: l.rate } : {}), ...(c.orderId && l.orderLineNo ? { lineNo: l.orderLineNo } : {}) })),
    ...(creditsOf(c).length ? { credits: creditsOf(c).map(creditBody) } : {}),
    ...(c.approveFloor && owner ? { approveFloor: true } : {}),
    ...(c.overrideHold && owner ? { overrideHold: true } : {}),
    ...(c.orderId ? { orderId: c.orderId, ...(c.orderVersion ? { orderVersion: c.orderVersion } : {}) } : {}),
  };
}

/** What an edit sends: the lines it should have now, each saved line by its id; the rest the server takes off. */
export function editBodyOf(c: Card, owner: boolean): InvoiceEdit {
  return {
    expectedTotal: c.edit!.total,
    ...(c.edit!.updatedAt ? { expectedUpdatedAt: c.edit!.updatedAt } : {}),
    notes: c.notes.trim(),
    // the date and the number, only when changed (decision 202)
    ...(c.invoiceDate !== c.edit!.invoiceDate ? { invoiceDate: c.invoiceDate } : {}),
    ...(c.invoiceNo != null && c.invoiceNo !== c.edit!.invoiceNo ? { invoiceNo: c.invoiceNo } : {}),
    lines: goodsOf(c).map((l) => ({ ...(l.lineId ? { lineId: l.lineId } : {}), itemId: l.itemId!, qty: l.qty!, unit: (l.unit ?? 'CS') as InvoiceEdit['lines'][number]['unit'], ...(l.rate !== null ? { unitPrice: l.rate } : {}) })),
    // every credit line it should have now: one not listed comes off
    credits: creditsOf(c).map((l) => ({ ...(l.lineId ? { lineId: l.lineId } : {}), ...creditBody(l) })),
    ...(c.approveFloor && owner ? { approveFloor: true } : {}),
    ...(c.overrideHold && owner ? { overrideHold: true } : {}),
    ...(c.confirmCredits ? { confirmCredits: true } : {}),
  };
}

/** QuickBooks' invoice form, fed by our engine; several side by side the way the office's Morales Invoices page does it.
 * With `edit`, the same form opens a saved invoice to change it (decision 170). */
export function InvoiceNewPage({ edit }: { edit?: Invoice } = {}) {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const owner = me?.role === 'owner';
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [cards, setCards] = useState<Card[]>(() => [edit ? editCard(edit) : newCard(params.get('customer'))]);
  const [saved, setSaved] = useState<Invoice | null>(null);
  // the Claude button (decision 137) opens this screen filled in: its invoices join what is already typed here
  const location = useLocation();
  /** the cards Claude filled: the note stays while one of them is still open */
  const [claudeFilled, setClaudeFilled] = useState<Set<string>>(() => new Set());
  const filledFrom = useRef<string | null>(null);
  useEffect(() => {
    const action = (location.state as Partial<AssistState> | null)?.[ASSIST_STATE];
    if (!action || action.kind !== 'invoice') return;
    // once per arrival (React may run this twice in development)
    if (filledFrom.current === location.key) return;
    filledFrom.current = location.key;
    const keys: string[] = [];
    {
      let next = cards.filter((c) => !c.done && (c.customerId !== null || filledOf(c).length > 0));
      for (const inv of action.invoices) {
        const lines = withTrailingBlank(inv.lines.map((l) => ({ ...blankLine(), itemId: l.itemId, qty: l.qty, rate: l.rate, unit: l.unit })));
        // "and two more limes": Claude sends the customer's whole invoice again; it replaces the open one, never a second
        const same = next.find((c) => !c.done && !c.orderId && c.customerId === inv.customerId);
        if (same) {
          keys.push(same.key);
          next = next.map((c) => (c.key === same.key ? { ...c, lines, notes: inv.notes ?? c.notes, attempt: null, error: null, approveFloor: false, overrideHold: false } : c));
        } else {
          const card = { ...newCard(inv.customerId), notes: inv.notes ?? '', lines };
          keys.push(card.key);
          next = [...next, card];
        }
      }
      setCards(next);
    }
    setClaudeFilled((cur) => new Set([...cur, ...keys]));
    // read once: a reload does not fill it again
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.key]);
  // the order day's orders (decision 175): one card per order, its lines as the order has them (a rate a person typed is
  // kept; the rest the engine's price, which the order follows until invoiced), to check and change before one save
  const [fromOrderDay, setFromOrderDay] = useState<{ skipped: OrderDayPreview[]; back: string; held: string[] } | null>(null);
  const orderDayFrom = useRef<string | null>(null);
  useEffect(() => {
    const got = (location.state as Record<string, unknown> | null)?.[ORDER_DAY_STATE] as OrderDayState | undefined;
    if (!got || orderDayFrom.current === location.key) return;
    orderDayFrom.current = location.key;
    const ready = got.orders.filter((o) => !o.problem && o.lines.length > 0);
    setCards(
      ready.map((o) => ({
        ...newCard(o.customerId),
        orderId: o.orderId,
        orderNo: o.orderNo,
        orderVersion: o.version,
        orderLocked: o.locked,
        lines: withTrailingBlank(o.lines.map((l) => ({ ...blankLine(), itemId: l.itemId, qty: l.qty, unit: l.unit, rate: l.typed ? l.unitPrice : null, desc: l.itemName, orderLineNo: l.lineNo, vendorShorts: l.vendorShorts }))),
      })),
    );
    setFromOrderDay({ skipped: got.orders.filter((o) => o.problem || o.lines.length === 0), back: got.back, held: got.held ?? [] });
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.key]);
  const customers = useQuery({ queryKey: qk.customers('', ''), queryFn: ({ signal }) => api.customers('', signal) });
  const multi = cards.length > 1;
  const open = cards.filter((c) => !c.done);
  // a saved invoice opened to change it is dirty only once something differs from what it was
  const [opened] = useState(() => (cards[0]?.edit ? JSON.stringify(editBodyOf(cards[0], owner)) : null));
  const dirty = opened !== null ? open.some((c) => JSON.stringify(editBodyOf(c, owner)) !== opened) : open.some((c) => filledOf(c).length > 0);
  const busy = cards.some((c) => c.saving);
  /** cards whose customer's prices are still loading */
  const [pricing, setPricing] = useState<Record<string, boolean>>({});
  const update = (key: string, change: (c: Card) => Card) => setCards((cur) => cur.map((c) => (c.key === key ? change(c) : c)));

  /** Save one card; resolves with the invoice, or null when it did not save (the card shows why). */
  async function saveCard(card: Card): Promise<Invoice | null> {
    // a card whose prices have not come yet shows "no price" on every line: it is not saved blind (end-user test)
    if (!readyOf(card) || pricing[card.key]) return null;
    // one made in QuickBooks becomes the app's with this save (decision 202): said once, before it happens
    if (card.edit?.fromQuickBooks && !window.confirm(t('takeOverConfirm'))) return null;
    const body = card.edit ? editBodyOf(card, owner) : bodyOf(card, owner);
    const s = JSON.stringify(body);
    const attempt = card.attempt?.body === s ? card.attempt : { body: s, key: newIdempotencyKey() };
    update(card.key, (c) => ({ ...c, attempt, saving: true, error: null }));
    try {
      const inv = card.edit ? await api.editInvoice(card.edit.id, body as InvoiceEdit, attempt.key) : await api.createInvoice(body as InvoiceCreate, attempt.key);
      // an edit's screen is still open on the invoice it began with: the invoice is read again after (no flash of
      // "someone changed it" for one's own save)
      if (!card.edit) qc.setQueryData(qk.invoice(inv.id), inv);
      update(card.key, (c) => ({ ...c, saving: false, done: inv, attempt: null }));
      return inv;
    } catch (e) {
      // a lost answer keeps the key so the retry is the same request; anything else starts fresh
      update(card.key, (c) => ({ ...c, saving: false, error: e, attempt: e instanceof ApiError && e.status === 0 ? attempt : null }));
      // an edit refused because the invoice changed meanwhile: read it again so the screen offers to open it again
      if (card.edit && e instanceof ApiError && e.status === 409) void qc.invalidateQueries({ queryKey: qk.invoice(card.edit.id) });
      return null;
    }
  }
  const afterSave = () => {
    void qc.invalidateQueries({ queryKey: ['invoices'] });
    void qc.invalidateQueries({ queryKey: ['customers'] });
    if (fromOrderDay) {
      void qc.invalidateQueries({ queryKey: ['forecast', 'order-day'] });
      void qc.invalidateQueries({ queryKey: ['orders'] });
    }
    if (edit) {
      void qc.invalidateQueries({ queryKey: qk.invoiceAdjustments(edit.id) });
      void qc.invalidateQueries({ queryKey: ['orders'] });
    }
  };

  async function saveSingle(next: After) {
    const card = cards[0]!;
    const inv = await saveCard(card);
    // a save that did not go leaves the screen as it is (refreshing the invoice under an open edit would not help)
    if (!inv) return;
    afterSave();
    if (next === 'new') {
      setSaved(inv);
      setCards([newCard()]);
      setFromOrderDay(null);
    } else navigate(`/invoices/${inv.id}${next === 'print' ? '?print=1' : ''}`);
  }
  async function saveAll() {
    // one at a time, in order: each card's result is its own; a card that fails keeps its lines
    for (const c of cards) if (readyOf(c)) await saveCard(c);
    afterSave();
  }
  const saveRef = useRef<() => void>(() => undefined);
  saveRef.current = () => void (multi ? saveAll() : saveSingle('close'));

  // leaving with typed lines asks first (reload and in-app links); Ctrl/Cmd+S saves
  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      if (dirty) e.preventDefault();
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        // the cell being typed in saves on blur; save once that has landed
        const active = document.activeElement;
        if (active instanceof HTMLElement && active.closest('.sheet')) active.blur();
        setTimeout(() => saveRef.current(), 0);
      }
    };
    const onClick = (e: MouseEvent) => {
      const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
      // a link on the screen too (a saved invoice's number, Print) leaves the other cards' typed lines behind
      if (!dirty || !a) return;
      if (!window.confirm(t('unsavedLeave'))) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('keydown', onKey);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, t]);

  useUnsaved(dirty, t('unsavedLeave'));

  // two cards for one customer on one date would be two invoices: flag it, the way the office's page does
  const dupKeys = new Set(
    cards
      // two orders of one store from the order day are two invoices on purpose
      .filter((c) => c.customerId && !c.done && !c.orderId)
      .filter((c, _i, all) => all.some((o) => o !== c && o.customerId === c.customerId && o.invoiceDate === c.invoiceDate))
      .map((c) => c.key),
  );
  const ready = cards.filter((c) => readyOf(c) && !pricing[c.key]);
  const [totals, setTotals] = useState<Record<string, number>>({});
  const total = round2(open.reduce((s, c) => s + (totals[c.key] ?? 0), 0));

  return (
    <div className={multi ? 'invoice-form multi' : 'invoice-form'}>
      <header className="form-head">
        <h1>{edit ? t('editInvoiceTitle').replace('{no}', String(edit.invoiceNo)) : multi ? t('invoicesTitle') : t('invoice')}</h1>
        <div className="form-head-right">
          <span className="label">{multi ? t('totalAllInvoices') : edit ? t('total') : t('balanceDue')}</span>
          <strong className="big-money" data-testid="form-total">{formatMoney(total, intlLocale)}</strong>
        </div>
      </header>
      {saved ? (
        <p className="note" role="status">
          {t('invoice')} {saved.invoiceNo} · {saved.customerName} · {formatMoney(saved.total, intlLocale)} ✓
        </p>
      ) : null}
      {fromOrderDay ? (
        <div className="note stack-sm" role="status" data-testid="order-day-check">
          <span>
            {t('orderDayToCheck')} <Link to={fromOrderDay.back}>{t('backToOrderDay')}</Link>
          </span>
          {fromOrderDay.skipped.length ? (
            <span className="muted">
              {t('orderDaySkipped')}: {fromOrderDay.skipped.map((o) => `${o.customerName}: ${o.problem ?? t('noLines')}`).join(' | ')}
            </span>
          ) : null}
          {fromOrderDay.held.length ? <span className="muted">{t('standingHeld').replace('{names}', fromOrderDay.held.join(', '))}</span> : null}
        </div>
      ) : null}
      {open.some((c) => claudeFilled.has(c.key)) ? (
        <p className="flash claude-filled" role="status">
          {t('claudeFilled')}
        </p>
      ) : null}

      <div className={multi ? 'invoice-cards' : undefined}>
        {cards.map((c) => (
          <InvoiceCard
            key={c.key}
            card={c}
            compact={multi}
            duplicate={dupKeys.has(c.key)}
            customers={customers.data ?? []}
            owner={owner}
            onChange={(change) => update(c.key, change)}
            onTotal={(n) => setTotals((cur) => (cur[c.key] === n ? cur : { ...cur, [c.key]: n }))}
            onPricing={(on) => setPricing((cur) => (!!cur[c.key] === on ? cur : { ...cur, [c.key]: on }))}
            onRemove={multi ? () => setCards((cur) => cur.filter((x) => x.key !== c.key)) : undefined}
          />
        ))}
      </div>

      <footer className="form-actions">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            if (!dirty || window.confirm(t('unsavedLeave'))) navigate(-1);
          }}
        >
          {t('cancel')}
        </button>
        {!edit ? (
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setCards((cur) => [...cur, newCard()])}>
            + {t('addInvoice')}
          </button>
        ) : null}
        <span className="spacer" />
        {multi && open.length === 0 ? (
          // every invoice made: no "Create 0 invoice(s)", the way back instead
          fromOrderDay ? (
            <Link className="btn btn-primary" to={fromOrderDay.back}>
              {t('allSaved')} · {t('backToOrderDay')}
            </Link>
          ) : (
            <span className="note">{t('allSaved')}</span>
          )
        ) : multi ? (
          <button type="button" className="btn btn-primary" disabled={busy || ready.length === 0} onClick={() => void saveAll()}>
            {t('createNInvoices').replace('{n}', String(ready.length))}
          </button>
        ) : (
          <>
            {!edit ? (
              <button type="button" className="btn btn-secondary" disabled={busy || ready.length === 0} onClick={() => void saveSingle('new')}>
                {t('saveAndNew')}
              </button>
            ) : null}
            <button type="button" className="btn btn-secondary" disabled={busy || ready.length === 0} onClick={() => void saveSingle('close')}>
              {t('saveAndClose')}
            </button>
            <button type="button" className="btn btn-primary" disabled={busy || ready.length === 0} onClick={() => void saveSingle('print')}>
              {t('saveAndPrint')}
            </button>
          </>
        )}
      </footer>
    </div>
  );
}

function InvoiceCard({ card, compact, duplicate, customers, owner, onChange, onTotal, onPricing, onRemove }: { card: Card; compact: boolean; duplicate: boolean; customers: CustomerSummary[]; owner: boolean; onChange: (change: (c: Card) => Card) => void; onTotal: (total: number) => void; onPricing: (loading: boolean) => void; onRemove: (() => void) | undefined }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [armed, setArmed] = useState(false);
  const { customerId } = card;
  const customer = useQuery({ queryKey: qk.customer(customerId ?? ''), queryFn: ({ signal }) => api.customer(customerId!, signal), enabled: !!customerId });
  const sheet = useQuery({ queryKey: qk.priceSheet(customerId ?? ''), queryFn: ({ signal }) => api.priceSheet(customerId!, signal), enabled: !!customerId });
  const pattern = useQuery({ queryKey: qk.pattern(customerId ?? ''), queryFn: ({ signal }) => api.customerPattern(customerId!, signal), enabled: !!customerId, retry: false });
  const tax = useQuery({ queryKey: qk.taxSettings, queryFn: ({ signal }) => api.taxSettings(signal), retry: false, staleTime: 60 * 60_000 });
  const byItem = useMemo(() => new Map((sheet.data ?? []).map((r) => [r.itemId, r])), [sheet.data]);
  const itemOptions: PickOption[] = useMemo(
    () => (sheet.data ?? []).map((r) => ({ value: r.itemId, label: r.itemName, hint: `${r.packUnit}${r.price !== null ? ` · ${formatMoney(r.price, intlLocale)}` : ''}`, search: `${r.category ?? ''} ${r.packDesc ?? ''}` })),
    [sheet.data, intlLocale],
  );
  const c: Customer | undefined = customer.data;
  /** the customer's prices are still on their way: nothing is picked or saved until they are here */
  const pricing = !!customerId && sheet.isPending;
  useEffect(() => onPricing(pricing), [pricing, onPricing]);
  const today = todayIso();
  const lines = card.lines;
  const set = (patch: Partial<Card>) => onChange((x) => ({ ...x, ...patch }));
  /** a unit as people say it: lb, each, bunch; a box keeps its code (CS, SK) */
  const unitLabel = (u: string): string => (u === 'LB' ? t('unitLb') : u === 'EA' ? t('unitEa') : u === 'BN' ? t('unitBn') : u === 'DZ' ? t('unitDz') : u);
  /** sold out of the box on this line (decision 166): pounds or each of a product sold loose */
  const looseOn = (l: InvLine) => {
    const p = l.itemId ? byItem.get(l.itemId) : undefined;
    // a product packed by the pound or each (its box is EA) sells its box: never "loose"
    return p?.loose && l.unit === p.loose.unit && l.unit !== p.packUnit ? p.loose : null;
  };
  /** the engine's price for the line's unit: the box price, or one pound/each of it */
  const engineOf = (l: InvLine): number | null => {
    const p = l.itemId ? byItem.get(l.itemId) : undefined;
    if (!p) return null;
    const loose = looseOn(l);
    // a unit the box content does not convert to has no price here (the server refuses or prices it on save); the
    // product's own pack unit is its box, whatever it is called
    if (!loose && l.unit && l.unit !== p.packUnit && ['LB', 'OZ', 'EA', 'DZ', 'BN'].includes(l.unit)) return null;
    return loose ? loose.price : p.price;
  };
  const rateOf = (l: InvLine): number | null => l.rate ?? engineOf(l);
  /** one unit of the line as a share of a box: 1 for a box, 0.04 for a pound of a 25 lb box */
  const perOf = (l: InvLine): number => looseOn(l)?.perPack ?? 1;
  /** the box's cost, floor and tier price in the line's unit */
  const inUnit = (l: InvLine, v: number | null | undefined): number | null => (v == null ? null : Math.round(v * perOf(l) * 10000) / 10000);
  const amountOf = (l: InvLine): number | null => {
    const r = rateOf(l);
    return r === null || l.qty === null ? null : round2(r * l.qty);
  };
  const filled = filledOf(card);
  const qtyMissing = qtyMissingOf(card);
  const subtotal = round2(filled.reduce((s, l) => s + (amountOf(l) ?? 0), 0));
  const taxRate = c?.taxable && tax.data ? tax.data.rate : 0;
  const taxAmt = round2(subtotal * taxRate);
  const total = round2(subtotal + taxAmt);
  // the page adds the cards up; each card says its own total once it knows it
  useEffect(() => onTotal(card.done ? 0 : total), [total, card.done, onTotal]);
  const dueDate = c ? addDays(card.invoiceDate, c.termsDays) : null;
  const sameDay = c?.recentInvoices.find((i) => i.date === card.invoiceDate) ?? null;

  const problems = card.error instanceof ApiError ? (card.error.problem.errors ?? []) : [];
  const badPaths = new Set(problems.map((x) => x.path));
  // the owner's tap passes a price under its floor or below cost, typed or the engine's (decision 122: never a wall nobody passes)
  const floorProblem = card.error instanceof ApiError && card.error.status === 422 && problems.some((p) => p.path.endsWith('unitPrice') || /under the floor|below cost/i.test(p.message));
  const holdProblem = card.error instanceof ApiError && card.error.status === 409 && /hold|credit limit/i.test(card.error.message);
  // fewer cases on an invoice that already has a credit for shorts: the same question the shorts ask (decision 170)
  const creditsProblem = problems.some((p) => p.path === 'confirmCredits');
  // the server names a line by where it is in what was sent: the lines, or the credits (decision 188)
  const pathOf = (l: InvLine, field: string) => (isCredit(l) ? `credits.${creditsOf(card).indexOf(l)}.${field}` : `lines.${goodsOf(card).indexOf(l)}.${field}`);
  const credits = creditsOf(card);
  const reasonMissing = reasonMissingOf(card);

  useEffect(() => {
    if (!armed) return;
    const id = setTimeout(() => setArmed(false), 8000);
    return () => clearTimeout(id);
  }, [armed]);

  function addUsual() {
    const usual = (pattern.data?.items ?? []).filter((i) => i.timesOrdered / Math.max(1, i.ofOrders) >= 0.5 && byItem.has(i.itemId));
    const have = new Set(lines.map((l) => l.itemId));
    // in the unit they buy it: the box, or pounds of a product sold by the pound (decision 166); a unit the product is
    // not sold in here is left out rather than added as boxes
    const add = usual
      .filter((u) => !have.has(u.itemId))
      .flatMap((u) => {
        const p = byItem.get(u.itemId)!;
        const loose = u.unit !== p.packUnit && ['LB', 'OZ', 'EA', 'DZ', 'BN'].includes(u.unit ?? '');
        const unit = !loose ? p.packUnit : u.unit === p.loose?.unit ? u.unit : null;
        return unit ? [{ ...blankLine(), itemId: u.itemId, qty: u.typicalQty, unit }] : [];
      });
    onChange((x) => ({ ...x, lines: withTrailingBlank([...x.lines.filter((l) => !isBlank(l)), ...add]), approveFloor: false, overrideHold: false, error: null }));
  }
  const row = (l: InvLine): PriceSheetRow | undefined => (l.itemId ? byItem.get(l.itemId) : undefined);
  /** what one box holds, asked once and saved on the product (decision 191); then the line is in that unit */
  async function askBox(l: InvLine, unit: 'LB' | 'EA') {
    const name = row(l)?.itemName ?? l.desc ?? '';
    const answer = window.prompt(t(unit === 'LB' ? 'boxHoldsLbAsk' : 'boxHoldsEaAsk').replace('{name}', name));
    const n = answer === null ? null : parseTyped(answer);
    // a box of produce holds up to a few hundred pounds or pieces: a slip of the finger would reprice every pound
    if (n === null || !(n > 0)) return;
    if (n > 2000) {
      onChange((x) => ({ ...x, error: new Error(t('boxTooBig')) }));
      return;
    }
    try {
      await api.patchItem(l.itemId!, { content: n, contentUnit: unit === 'LB' ? 'lb' : 'ct' });
      // every customer's sheet now knows the box (several invoices side by side, decision 62)
      await qc.invalidateQueries({ queryKey: ['customers', 'price-sheet'] });
      // the same amount in the new unit: 2 boxes of 36 lb are 72 lb (decision 178)
      onChange((x) => ({
        ...x,
        lines: x.lines.map((y) => (y.key === l.key ? { ...fresh(y), unit, rate: null, qty: y.qty === null ? null : Math.round(y.qty * n * 1000) / 1000 } : y)),
        error: null,
      }));
    } catch (err) {
      onChange((x) => ({ ...x, error: err }));
    }
  }
  /** on hand for this line: a saved line's own cases are already off the shelf, and are still there for it */
  const onHandFor = (l: InvLine, onHand: number): number => Math.round((onHand + (l.saved ?? 0) * perOf(l)) * 1000) / 1000;

  const all: (SheetColumn<InvLine> & { wide?: boolean })[] = [
    { key: 'no', header: '#', width: '32px', align: 'right', render: (_l, i) => i + 1 },
    {
      key: 'item',
      header: t('productService'),
      width: compact ? '38%' : '22%',
      render: (l) => (l.itemId ? (row(l)?.itemName ?? l.desc ?? '') : <span className="muted">{pricing ? t('loadingPrices') : t('pickProduct')}</span>),
      // a product is picked once the customer's prices are here: typed before, it matched nothing and was lost (end-user test)
      canEdit: () => !pricing && !sheet.isError,
      edit: {
        kind: 'pick',
        options: itemOptions,
        placeholder: t('typeToSearch'),
        get: () => '',
        // clearing the product clears the line: a quantity with no product would be quietly left off the invoice
        set: (l, v) => (v === '' ? { ...fresh(l), itemId: null, qty: null, rate: null } : v === l.itemId ? l : { ...fresh(l), itemId: v, rate: null, qty: l.qty ?? 1, unit: byItem.get(v)?.packUnit ?? 'CS' }),
      },
      cellClass: (l) => (badPaths.has(pathOf(l, 'itemId')) ? 'bad' : undefined),
    },
    { key: 'desc', header: t('lineDescription'), width: '8%', wide: true, render: (l) => row(l)?.packDesc ?? '' },
    {
      key: 'qty',
      header: t('qty'),
      width: '64px',
      align: 'right',
      render: (l) => (l.qty === null ? '' : formatQty(l.qty)),
      edit: {
        kind: 'number',
        get: (l) => (l.qty === null ? '' : String(l.qty)),
        set: (l, v) => {
          const q = parseNum(v);
          if (q !== null && q < 0) return null;
          const { switchedFrom: _was, ...rest } = l;
          return { ...rest, qty: q };
        },
      },
      // a line the vendor is short of (decision 176) stands out until its quantity is looked at
      cellClass: (l) => (badPaths.has(pathOf(l, 'qty')) || (l.itemId !== null && (l.qty === null || l.qty <= 0)) ? 'bad' : l.vendorShorts?.length ? 'warn' : undefined),
    },
    {
      key: 'unit',
      header: t('unitCol'),
      width: '52px',
      // a product sold out of the box picks its unit here: the box, or lb/each (decision 166); the rest show their box
      render: (l) => (l.itemId ? <>{unitLabel(l.unit ?? row(l)?.packUnit ?? '')}{row(l)?.loose?.offered || row(l)?.boxUnknown ? <span className="muted"> ▾</span> : null}</> : ''),
      canEdit: (l) => !!row(l)?.loose?.offered || !!row(l)?.boxUnknown,
      edit: {
        kind: 'pick',
        options: (l) => {
          const p = row(l);
          if (p?.loose?.offered) return [{ value: p.packUnit, label: unitLabel(p.packUnit) }, { value: p.loose.unit, label: unitLabel(p.loose.unit) }];
          // what the box holds is not known yet (decision 191): lb or each asks for it once
          if (p?.boxUnknown) return [{ value: p.packUnit, label: unitLabel(p.packUnit) }, { value: '?LB', label: t('unitLbSetBox') }, { value: '?EA', label: t('unitEaSetBox') }];
          return [];
        },
        get: () => '',
        // another unit is another price: a rate typed for the box is not kept for a pound. The quantity is the same
        // amount in the new unit (2 cases of 25 lb are 50 lb), never the same number (2 lb for $2.80, the end-user test)
        set: (l, v) => {
          if (!v || v === l.unit) return l;
          if (v.startsWith('?') && l.itemId) {
            void askBox(l, v.slice(1) as 'LB' | 'EA');
            return l;
          }
          const p = row(l);
          const per = p?.loose?.perPack ?? null;
          const was = l.unit ?? p?.packUnit;
          const toLoose = !!p?.loose && v === p.loose.unit && was !== p.loose.unit;
          const toBox = !!p?.loose && v === p.packUnit && was === p.loose.unit;
          // back to the unit it came from, nothing typed meanwhile: the quantity it had, not a rounding of a rounding
          const back = l.switchedFrom;
          if (back && back.unit === v) {
            const { switchedFrom: _was, ...rest } = fresh(l);
            return { ...rest, unit: v, rate: null, qty: back.qty };
          }
          const qty = l.qty === null || per === null ? l.qty : toLoose ? Math.round((l.qty / per) * 100) / 100 : toBox ? Math.round(l.qty * per * 1000) / 1000 : l.qty;
          return { ...fresh(l), unit: v, rate: null, qty, ...(was ? { switchedFrom: { unit: was, qty: l.qty } } : {}) };
        },
      },
    },
    {
      key: 'rate',
      header: t('rate'),
      hint: t('rateHint'),
      width: '88px',
      align: 'right',
      render: (l) => {
        const r = rateOf(l);
        if (r === null) return l.itemId ? <span className="muted">{t('noPrice')}</span> : '';
        return l.rate === null ? <span className="auto-rate">{formatMoney(r, intlLocale)}</span> : formatMoney(r, intlLocale);
      },
      edit: {
        kind: 'number',
        get: (l) => {
          const r = rateOf(l);
          return r === null ? '' : String(r);
        },
        set: (l, v) => {
          const r = parseNum(v);
          const engine = engineOf(l);
          const typed = r === null ? null : round2(r);
          // a credit cleared stays a credit: billing the case instead is a positive rate typed, never a slip of Delete
          if (typed === null && isCredit(l)) return l;
          // a rate below zero is a credit (decision 188); a sale turned credit, or back, is another line (the saved one comes off)
          const base = (typed !== null && typed < 0) !== isCredit(l) ? fresh(l) : l;
          // a saved line keeps the price it shows, even one that is the engine's today (a cleared cell takes the engine's)
          return { ...base, rate: typed === null || (typed === engine && !base.lineId) ? null : typed };
        },
      },
      cellClass: (l) => {
        if (badPaths.has(pathOf(l, 'unitPrice'))) return 'bad';
        // a credit is not held to cost or the floor
        if (isCredit(l)) return 'credit';
        const p = row(l);
        const r = rateOf(l);
        if (!p || r === null) return undefined;
        const cost = inUnit(l, p.cost);
        const floor = inUnit(l, p.floorPrice);
        if ((cost !== null && r < ceilCents(cost)) || (floor !== null && r < ceilCents(floor))) return 'bad';
        // a pound is held to the engine's pound price, not the box's tier price
        const tier = looseOn(l) ? looseOn(l)!.price : p.tierPrice;
        if (tier !== null && r < tier) return 'warn';
        return l.rate !== null ? 'good' : undefined;
      },
    },
    {
      key: 'amount',
      header: t('amount'),
      width: '92px',
      align: 'right',
      render: (l) => {
        const a = amountOf(l);
        return a === null ? '' : <strong>{formatMoney(a, intlLocale)}</strong>;
      },
    },
    // a pound line's tier price is the box's: said on hover
    { key: 'tier', header: t('tierPrice'), width: '80px', align: 'right', wide: true, render: (l) => (row(l)?.tierPrice != null ? <span title={looseOn(l) ? `${formatMoney(row(l)!.tierPrice!, intlLocale)} / ${unitLabel(row(l)!.packUnit)}` : undefined}>{formatMoney(row(l)!.tierPrice!, intlLocale)}</span> : '') },
    {
      key: 'last',
      header: t('lastPaid'),
      width: compact ? '100px' : '104px',
      align: 'right',
      render: (l) => {
        const p = row(l);
        if (p?.lastPrice == null || !p.lastDate) return '';
        // on a card there is room for the price; the day shows on hover
        return compact ? <span title={shortDate(p.lastDate, intlLocale)}>{formatMoney(p.lastPrice, intlLocale)}</span> : `${formatMoney(p.lastPrice, intlLocale)} · ${shortDate(p.lastDate, intlLocale)}`;
      },
    },
    {
      key: 'margin',
      header: t('marginPct'),
      width: '64px',
      align: 'right',
      wide: true,
      render: (l) => {
        const p = row(l);
        const r = rateOf(l);
        const cost = inUnit(l, p?.cost);
        if (!p || cost === null || r === null || r <= 0) return '';
        return `${Math.round(((r - cost) / r) * 1000) / 10}%`;
      },
      cellClass: (l) => {
        const r = rateOf(l);
        const cost = inUnit(l, row(l)?.cost);
        return !isCredit(l) && cost !== null && r !== null && r < ceilCents(cost) ? 'bad' : undefined;
      },
    },
    {
      key: 'onhand',
      header: t('onHand'),
      hint: t('onHandFlagHint'),
      width: '62px',
      align: 'right',
      // on every card, side by side too: the flag that we are short of what the invoice sells (JL, 2026-10-25)
      render: (l) => {
        const p = row(l);
        // a credit takes nothing off the shelf
        if (!p || isCredit(l)) return '';
        const have = onHandFor(l, p.onHand);
        if (l.qty !== null && l.qty > 0 && have <= 0) return <strong>{t('outOfStock')}</strong>;
        return formatQty(have);
      },
      cellClass: (l) => {
        const p = row(l);
        if (!p || isCredit(l) || l.qty === null || l.qty <= 0) return undefined;
        const have = onHandFor(l, p.onHand);
        // on hand is in boxes: 20 lb of a 25 lb box is 0.8 of one
        return have <= 0 ? 'bad' : l.qty * perOf(l) > have ? 'warn' : undefined;
      },
    },
  ];
  const columns = compact ? all.filter((x) => !x.wide) : all;

  if (card.done) {
    return (
      <section className="inv-card done" data-testid="invoice-card">
        <p className="ok-band" role="status">
          ✓ {t('invoice')} <Link to={`/invoices/${card.done.id}`}>{card.done.invoiceNo}</Link> · {card.done.customerName} · {formatMoney(card.done.total, intlLocale)}
          {' · '}
          <Link to={`/invoices/${card.done.id}?print=1`}>{t('print')}</Link>
        </p>
        {onRemove ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onRemove}>
            {t('remove')}
          </button>
        ) : null}
      </section>
    );
  }

  return (
    <section className={duplicate ? 'inv-card dup' : 'inv-card'} data-testid="invoice-card">
      {onRemove ? (
        <div className="card-bar">
          <strong>{c?.name ?? t('newInvoice')}</strong>
          {card.orderNo ? <span className="muted">{t('fromOrderNo').replace('{no}', String(card.orderNo))}</span> : null}
          <span className="spacer" />
          <strong>{formatMoney(total, intlLocale)}</strong>
          <button type="button" className="icon-btn" aria-label={`${t('remove')}: ${c?.name ?? t('newInvoice')}`} onClick={() => (filled.length === 0 || window.confirm(t('unsavedLeave'))) && onRemove()}>
            ✕
          </button>
        </div>
      ) : null}
      <div className="form-top">
        {card.edit || card.orderId ? (
          // a saved invoice keeps its customer and its date: another customer is another invoice; an order's card is
          // that customer's order
          <label className="field">
            <span>{t('customer')}</span>
            <input readOnly value={c?.name ?? ''} />
          </label>
        ) : (
          <CustomerCombo
            customers={customers}
            value={customerId}
            onChange={(id) => {
              if (id === customerId) return;
              if (filled.length > 0 && !window.confirm(t('changeCustomerClears'))) return;
              set({ customerId: id, lines: [blankLine()], error: null, approveFloor: false, overrideHold: false });
            }}
          />
        )}
        {!compact ? (
          <div className="field">
            <span>{t('billTo')}</span>
            <div className="bill-to" data-testid="bill-to">
              {c ? (
                <>
                  <strong>{c.name}</strong>
                  {c.location?.addressLine1 ? <span>{c.location.addressLine1}</span> : null}
                  {c.location ? <span>{[c.location.city, c.location.state, c.location.zip].filter(Boolean).join(', ')}</span> : null}
                </>
              ) : (
                <span className="muted">—</span>
              )}
            </div>
          </div>
        ) : null}
        <label className="field">
          <span>{t('terms')}</span>
          <input readOnly value={c ? (c.termsDays === 0 ? t('dueOnReceipt') : t('netDays').replace('{n}', String(c.termsDays))) : ''} />
        </label>
        <div className={card.invoiceDate !== today && !card.edit ? 'field backdated' : 'field'}>
          <span>
            <label htmlFor={`${card.key}-date`}>{t('invoiceDate')}</label>
            {card.invoiceDate !== today && !card.edit ? (
              <>
                {' '}· {t('backdated')} ·{' '}
                <button type="button" className="link" onClick={() => set({ invoiceDate: today })}>
                  {t('setToToday')}
                </button>
              </>
            ) : null}
          </span>
          <input id={`${card.key}-date`} type="date" value={card.invoiceDate} onChange={(e) => e.target.value && set({ invoiceDate: e.target.value })} />
        </div>
        {!compact ? (
          <>
            <label className="field">
              <span>{t('dueDate')}</span>
              <input readOnly value={dueDate ? formatDate(dueDate, intlLocale, 'date') : ''} />
            </label>
            {card.edit ? (
              // a saved invoice's number is changed here too (decision 202): it must be free in the app and in QuickBooks
              <label className="field">
                <span>{t('invoiceNoLabel')}</span>
                <input
                  inputMode="numeric"
                  value={card.invoiceNo ?? ''}
                  className={badPaths.has('invoiceNo') ? 'bad' : undefined}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '');
                    set({ invoiceNo: v ? Number(v) : null });
                  }}
                />
              </label>
            ) : null}
            {card.edit ? null : (
            <label className="field">
              <span>{t('cameIn')}</span>
              <select value={card.channel} onChange={(e) => set({ channel: e.target.value as Channel })}>
                {CHANNELS.map((ch) => (
                  <option key={ch} value={ch}>
                    {t(`channel_${ch}`)}
                  </option>
                ))}
              </select>
            </label>
            )}
          </>
        ) : null}
        {c ? (
          <div className="customer-facts" data-testid="customer-facts">
            <span>
              <span className="label">{t('openBalance')}</span> {c.balance !== null ? formatMoney(c.balance, intlLocale) : '—'}
            </span>
            {c.creditLimit !== null ? (
              <span>
                <span className="label">{t('creditLimit')}</span> {formatMoney(c.creditLimit, intlLocale)}
              </span>
            ) : null}
            <span>
              <span className="label">{t('tierCol')}</span> {c.priceTier}
            </span>
            {c.onHold ? (
              <span className="badge badge-danger">
                {t('onHold')}
                {c.holdReason ? `: ${c.holdReason}` : ''}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      <InvoiceMakingBand />
      {/* the order number: in the card's bar when several are side by side; here when one is alone, or picked already */}
      {card.orderNo && (!onRemove || card.orderLocked) ? (
        <p className={card.orderLocked ? 'warn-band' : 'muted'} role="note">
          {t('fromOrderNo').replace('{no}', String(card.orderNo))}
          {card.orderLocked ? ` · ${t('orderLockedNote')}` : ''}
        </p>
      ) : null}
      {card.lines.some((l) => l.vendorShorts?.length) ? (
        <p className="warn-band" role="note" data-testid="vendor-short-lines">
          {t('vshortOnCard')}{' '}
          {card.lines
            .filter((l) => l.vendorShorts?.length)
            .map((l) => `${row(l)?.itemName ?? l.desc ?? ''} (${formatQty(l.qty ?? 0)}) · ${l.vendorShorts!.map((v) => `${t('vshortComing').replace('{vendor}', v.vendor).replace('{coming}', formatQty(v.coming)).replace('{ordered}', formatQty(v.ordered))} (${t(`vshortSaid_${v.source}`)})`).join(', ')}`)
            .join('; ')}
        </p>
      ) : null}
      {sameDay && !card.edit && !card.orderId ? (
        <p className="warn-band" role="note">
          {t('alreadyInvoicedOn').replace('{no}', String(sameDay.invoiceNo)).replace('{total}', formatMoney(sameDay.total, intlLocale))}
        </p>
      ) : null}
      {duplicate ? (
        <p className="warn-band" role="note">
          {t('twoCardsSameDay')}
        </p>
      ) : null}

      {customerId && sheet.isError ? (
        <p className="warn-band" role="alert" data-testid="prices-failed">
          {t('pricesFailed')}{' '}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void sheet.refetch()}>
            {t('retry')}
          </button>
        </p>
      ) : null}
      {customerId ? (
        <Sheet
          label={t('invoiceLines')}
          columns={columns}
          rows={lines}
          rowKey={(l) => l.key}
          minWidth={compact ? '560px' : '1060px'}
          onRowsChange={(next) => onChange((x) => ({ ...x, lines: withTrailingBlank(next), approveFloor: false, overrideHold: false, confirmCredits: false, error: null }))}
        />
      ) : (
        <p className="muted pick-first">{t('pickCustomerFirst')}</p>
      )}
      {credits.length ? (
        <div className="credit-lines" data-testid="credit-lines">
          <strong>{t('creditLinesTitle')}</strong>
          <p className="muted">{t('creditLinesHint')}</p>
          {credits.map((l) => (
            <label key={l.key} className={`credit-line${!l.creditReason || badPaths.has(pathOf(l, 'reason')) ? ' bad' : ''}`}>
              <span>
                {row(l)?.itemName ?? l.desc ?? ''} · {formatMoney(amountOf(l) ?? 0, intlLocale)}
              </span>
              <select
                aria-label={`${t('creditWhy')} ${row(l)?.itemName ?? l.desc ?? ''}`}
                value={l.creditReason ?? ''}
                onChange={(e) => {
                  const why = e.target.value as CreditReason | '';
                  onChange((x) => ({ ...x, lines: x.lines.map((y) => (y.key !== l.key ? y : why ? { ...y, creditReason: why } : (({ creditReason: _drop, ...rest }) => rest)(y))), error: null }));
                }}
              >
                <option value="">{t('creditWhy')}</option>
                {CREDIT_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {t(`reason_${r}`)}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
      ) : null}
      <div className="line-actions">
        <button type="button" className="btn btn-secondary btn-sm" disabled={!pattern.data?.items.length} onClick={addUsual}>
          {t('addUsualItems')}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" disabled={filled.length === 0} onClick={() => onChange((x) => ({ ...x, lines: withTrailingBlank(x.lines.filter((l) => !isBlank(l)).slice(0, -1)) }))}>
          {t('removeLastLine')}
        </button>
        <button
          type="button"
          className={armed ? 'btn btn-warn btn-sm' : 'btn btn-secondary btn-sm'}
          disabled={filled.length === 0}
          onClick={() => {
            if (!armed) return setArmed(true);
            setArmed(false);
            set({ lines: [blankLine()] });
          }}
        >
          {armed ? t('pressAgainToClear') : t('clearAllLines')}
        </button>
      </div>

      <div className="form-bottom">
        <label className="field">
          <span>{t('messageOnInvoice')}</span>
          <textarea rows={2} maxLength={1000} value={card.notes} onChange={(e) => set({ notes: e.target.value })} />
        </label>
        <dl className="form-totals">
          <div>
            <dt>{t('subtotal')}</dt>
            <dd>{formatMoney(subtotal, intlLocale)}</dd>
          </div>
          {taxRate > 0 ? (
            <div>
              <dt>
                {tax.data!.name} ({Math.round(taxRate * 100000) / 1000}%)
              </dt>
              <dd>{formatMoney(taxAmt, intlLocale)}</dd>
            </div>
          ) : null}
          <div className="grand">
            <dt>{t('total')}</dt>
            <dd>{formatMoney(total, intlLocale)}</dd>
          </div>
        </dl>
      </div>

      {qtyMissing.length > 0 ? (
        <p className="field-error" role="alert">
          {t('qtyNeeded')}
        </p>
      ) : null}
      {reasonMissing.length > 0 ? (
        <p className="field-error" role="alert" data-testid="credit-reason-needed">
          {t('creditReasonNeeded')}
        </p>
      ) : null}
      {total < 0 ? (
        <p className="field-error" role="alert">
          {t('creditMoreThanInvoice')}
        </p>
      ) : null}
      {card.saving ? <p className="muted">{t('sending')}</p> : null}
      {card.error ? (
        <div className="stack-sm">
          <ErrorBox error={card.error} />
          {problems.length ? (
            <ul className="problems">
              {problems.map((p) => (
                <li key={p.path}>{p.message}</li>
              ))}
            </ul>
          ) : null}
          {floorProblem && owner ? (
            <label className="check">
              <input type="checkbox" checked={card.approveFloor} onChange={(e) => set({ approveFloor: e.target.checked })} /> {t('approveUnderFloor')}
            </label>
          ) : null}
          {creditsProblem ? (
            <label className="check">
              <input type="checkbox" checked={card.confirmCredits === true} onChange={(e) => set({ confirmCredits: e.target.checked })} /> {t('shortsConfirmCredits')}
            </label>
          ) : null}
          {holdProblem && owner ? (
            <label className="check">
              <input type="checkbox" checked={card.overrideHold} onChange={(e) => set({ overrideHold: e.target.checked })} /> {t('invoiceAnyway')}
            </label>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/** The customer box: type to find, arrows to choose, Enter to take it; the tier and the balance beside each name. */
function CustomerCombo({ customers, value, onChange }: { customers: CustomerSummary[]; value: string | null; onChange: (id: string | null) => void }) {
  const { t, intlLocale } = useLocale();
  const chosen = customers.find((c) => c.id === value) ?? null;
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const listId = useMemo(() => `cust-${Math.random().toString(36).slice(2, 8)}`, []);
  const options = useMemo(
    () => customers.filter((c) => c.active && c.stage !== 'lost').map((c) => ({ value: c.id, label: c.name, hint: `${c.priceTier}${c.balance ? ` · ${formatMoney(c.balance, intlLocale)}` : ''}`, search: c.shortName ?? '' })),
    [customers, intlLocale],
  );
  const matches = filterOptions(options, q);
  const take = (id: string) => {
    onChange(id);
    setOpen(false);
    setQ('');
  };
  return (
    <div className="field combo">
      <span>
        <label htmlFor={`${listId}-input`}>{t('customer')}</label>
      </span>
      <input
        id={`${listId}-input`}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        autoComplete="off"
        placeholder={t('chooseCustomer')}
        value={open ? q : (chosen?.name ?? '')}
        onFocus={() => {
          setOpen(true);
          setHi(0);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onChange={(e) => {
          setQ(e.target.value);
          setHi(0);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') setHi((h) => Math.min(matches.length - 1, h + 1));
          else if (e.key === 'ArrowUp') setHi((h) => Math.max(0, h - 1));
          else if (e.key === 'Enter' && q.trim() !== '' && matches[hi]) take(matches[hi].value);
          else if (e.key === 'Escape') setOpen(false);
          else return;
          e.preventDefault();
        }}
      />
      {open ? (
        <ul id={listId} className="combo-list" role="listbox" aria-label={t('customer')}>
          {matches.map((o, i) => (
            <li
              key={o.value}
              role="option"
              aria-selected={i === hi}
              className={i === hi ? 'hi' : undefined}
              onMouseDown={(e) => {
                e.preventDefault();
                take(o.value);
              }}
            >
              <span>{o.label}</span>
              <span className="pick-hint">{o.hint}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
